import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { getSessions, getChatHistory, type SessionSummary } from '../api/chat'
import TherapistAvatar from '../components/chat/TherapistAvatar'

export default function HistoryPage() {
  const navigate = useNavigate()
  const [selectedSession, setSelectedSession] = useState<SessionSummary | null>(null)

  // Fetch sessions
  const { data: sessionsData, isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => getSessions(50, 0),
  })

  // Fetch messages for selected session
  const { data: messagesData, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['sessionMessages', selectedSession?.id],
    queryFn: () => getChatHistory(selectedSession?.id, 100),
    enabled: !!selectedSession,
  })

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    })
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getTherapistName = (type: string): string => {
    const names: Record<string, string> = {
      wendy: 'Венди',
      artem: 'Артём',
      una: 'Уна',
      finch: 'Гарольд',
      harold: 'Гарольд',
    }
    return names[type] || type
  }

  return (
    <div data-testid="history-page" className="min-h-screen bg-gradient-calm">
      {/* Header */}
      <header className="glass border-b border-neutral-200/50 sticky top-0 z-10 safe-area-pt">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <button
            aria-label="Назад"
            onClick={() => selectedSession ? setSelectedSession(null) : navigate('/chat')}
            className="p-2 -ml-2 hover:bg-neutral-100 rounded-full transition-colors"
          >
            <svg className="w-6 h-6 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="ml-2 text-lg font-semibold text-neutral-800">
            {selectedSession ? `Сессия ${formatDate(selectedSession.started_at)}` : 'История сессий'}
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 animate-fade-in safe-area-pb">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Session list view */}
        {!selectedSession && !isLoading && (
          <div className="space-y-3">
            {sessionsData?.sessions.map((session) => (
              <button
                key={session.id}
                data-testid={`session-card-${session.id}`}
                onClick={() => setSelectedSession(session)}
                className="w-full card hover:shadow-soft-lg transition-all text-left"
              >
                <div className="flex items-start gap-4">
                  {/* Therapist avatar */}
                  <TherapistAvatar
                    therapist={{ type: session.therapist, name: getTherapistName(session.therapist) }}
                    size="md"
                  />

                  {/* Session info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-neutral-800">
                        {getTherapistName(session.therapist)}
                      </h3>
                      <span className="text-sm text-neutral-500">
                        {formatDate(session.started_at)}
                      </span>
                    </div>

                    {/* Summary or message count */}
                    {session.summary ? (
                      <p className="text-sm text-neutral-600 line-clamp-2">
                        {session.summary}
                      </p>
                    ) : (
                      <p className="text-sm text-neutral-500">
                        {session.message_count} сообщений
                      </p>
                    )}

                    {/* Status badge */}
                    <div className="mt-2 flex items-center gap-2">
                      <span className={clsx(
                        'text-xs px-2 py-0.5 rounded-full',
                        session.state === 'active'
                          ? 'bg-success/10 text-success-dark'
                          : 'bg-neutral-100 text-neutral-500'
                      )}>
                        {session.state === 'active' ? 'Активная' : 'Завершена'}
                      </span>
                      {session.ended_at && (
                        <span className="text-xs text-neutral-500">
                          {formatTime(session.started_at)} — {formatTime(session.ended_at)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <svg className="w-5 h-5 text-neutral-500 flex-shrink-0 mt-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}

            {/* Empty state */}
            {sessionsData?.sessions.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg className="w-8 h-8 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-medium text-neutral-800 mb-1">Нет истории</h3>
                <p className="text-sm text-neutral-500">
                  Начните разговор с терапевтом, чтобы увидеть историю сессий
                </p>
              </div>
            )}
          </div>
        )}

        {/* Session detail view */}
        {selectedSession && (
          <div className="space-y-4">
            {/* Session summary card */}
            <div className="card bg-primary-50 border border-primary-100">
              <div className="flex items-center gap-3">
                <TherapistAvatar
                  therapist={{ type: selectedSession.therapist, name: getTherapistName(selectedSession.therapist) }}
                  size="md"
                />
                <div>
                  <h3 className="font-medium text-neutral-800">
                    Сессия с {getTherapistName(selectedSession.therapist)}
                  </h3>
                  <p className="text-sm text-neutral-600">
                    {formatDate(selectedSession.started_at)} • {selectedSession.message_count} сообщений
                  </p>
                </div>
              </div>
              {selectedSession.summary && (
                <p className="mt-3 text-sm text-neutral-700 border-t border-primary-100 pt-3">
                  {selectedSession.summary}
                </p>
              )}
            </div>

            {/* Messages loading */}
            {isLoadingMessages && (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* Messages list */}
            {messagesData?.messages.map((message) => (
              <div
                key={message.id}
                className={clsx(
                  'flex gap-3',
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {message.role === 'assistant' && (
                  <TherapistAvatar
                    therapist={{ type: selectedSession.therapist, name: getTherapistName(selectedSession.therapist) }}
                    size="sm"
                  />
                )}
                <div
                  className={clsx(
                    'max-w-[80%] px-4 py-3 rounded-2xl',
                    message.role === 'user'
                      ? 'bg-primary-500 text-white rounded-br-md'
                      : 'bg-white shadow-soft rounded-bl-md'
                  )}
                >
                  <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                  <p className={clsx(
                    'text-xs mt-1',
                    message.role === 'user' ? 'text-white/70' : 'text-neutral-500'
                  )}>
                    {formatTime(message.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
